import * as React from "react"
import { Head, Link, router, useForm } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Autocomplete } from "@/components/ui/autocomplete"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CheckboxOption } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import { useUserSearch } from "@/hooks/use-user-search"
import {
  ACCESS_LEVEL_LABELS,
  ACCESS_LEVEL_TONES,
  type LevelOption,
  type UserAccessGroup,
  type UserAppAccess,
} from "@/types/access"

type UserDetail = {
  id: number
  name: string
  username: string | null
  email: string | null
  position: string | null
  organization: string | null
  org_claim: string | null
  /** What the app uses: the override if there is one, otherwise the claim. */
  department: string | null
  department_claim: string | null
  department_override: string | null
  admin: boolean
  timezone: string | null
  last_login_at: string | null
  created_at: string
  updated_at: string
  /** Sessions still within their idle timeout and absolute lifetime. */
  open_sessions: number
}

/** Who fills out this person's Travel Tracker for them (milestone 30). */
type TravelSupport = {
  /** False for an account known to be outside IUPAT, which has no tracker. */
  available: boolean
  /** True until this person signs in and Duo says where they work. */
  organization_unknown: boolean
  helpers: {
    id: number
    name: string
    email: string | null
    organization: string | null
    href: string
    granted_by: string | null
    granted_at: string
    /** False when the helper has no Edit access to Travel Tracker of their own. */
    can_fill_out: boolean
  }[]
}

export default function AdminUserShow({
  user,
  department_suggestions,
  access,
  access_groups,
  levels,
  travel_support,
}: {
  user: UserDetail
  department_suggestions: string[]
  access: UserAppAccess[]
  access_groups: UserAccessGroup[]
  levels: LevelOption[]
  travel_support: TravelSupport
}) {
  return (
    <>
      <Head title={user.name}>
        <meta name="description" content={`User detail for ${user.name}.`} />
        <meta property="og:title" content={user.name} />
        <meta property="og:description" content={`User detail for ${user.name}.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{user.name}</h1>
              <p className="mt-1">User #{user.id}</p>
            </div>
            <div className="flex items-center gap-2">
              {user.admin && <Badge tone="accent">Admin</Badge>}
              <Link
                href="/admin/users"
                className="text-sm text-ink-muted no-underline hover:text-ink-display"
              >
                Back to users
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <FlashNotice />
        </div>

        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
          <DetailRow label="Username" value={user.username ?? "—"} />
          <DetailRow label="Email" value={user.email ?? "—"} />
          <DetailRow label="Position" value={user.position ?? "—"} />
          <DetailRow label="Organization" value={user.organization ?? "—"} />
          <DetailRow label="Duo organization claim" value={user.org_claim ?? "—"} />
          <DetailRow label="Department" value={user.department ?? "—"} />
          <DetailRow label="Duo department claim" value={user.department_claim ?? "—"} />
          <DetailRow label="Admin" value={user.admin ? "Yes" : "No"} />
          <DetailRow label="Timezone" value={user.timezone ?? "—"} />
          <DetailRow
            label="Last sign-in"
            value={user.last_login_at ? formatDateTime(user.last_login_at) : "—"}
          />
          <DetailRow label="First signed in" value={formatDateTime(user.created_at)} />
          <DetailRow label="Updated" value={formatDateTime(user.updated_at)} />
        </dl>

        <AccessPanel user={user} access={access} groups={access_groups} levels={levels} />
        <TravelSupportPanel user={user} support={travel_support} />
        <DepartmentOverride user={user} suggestions={department_suggestions} />
        <SessionRevocation user={user} />
      </AdminShell>
    </>
  )
}

const USE_GROUPS = ""

/**
 * Travel Tracker support: the people who may fill out this person's tracker.
 *
 * An assistant files for an executive. It is set here, on the owner's page, and
 * nowhere else — naming your own helper would be a way to reach somebody else's
 * report. Being named grants nothing on its own: the helper still needs Travel
 * Tracker access in their own right, and the list says when they do not.
 */
function TravelSupportPanel({ user, support }: { user: UserDetail; support: TravelSupport }) {
  const [query, setQuery] = React.useState("")
  const { suggestions, loading } = useUserSearch(query)

  if (!support.available) return null

  const named = new Set(support.helpers.map((helper) => helper.id))

  const add = (helperId: number) => {
    router.post(
      `/admin/users/${user.id}/travel_support`,
      { helper_id: helperId },
      { preserveScroll: true, onSuccess: () => setQuery("") },
    )
  }

  const remove = (helperId: number) => {
    router.delete(`/admin/users/${user.id}/travel_support/${helperId}`, { preserveScroll: true })
  }

  return (
    <section className="mt-10 border-t border-hairline pt-6">
      <h2>Travel Tracker support</h2>
      <p className="mt-1 max-w-prose text-sm text-ink-muted">
        People who can fill out {user.name}&rsquo;s Travel Tracker for them. Each one
        chooses whose report they are filling out on the tracker itself, and every day
        they record says they filed it. Nothing else about {user.name}&rsquo;s account is
        shared.
      </p>

      {support.organization_unknown && (
        <p className="mt-2 max-w-prose text-xs text-ink-muted">
          {user.name} has never signed in, so the Portal doesn&rsquo;t know their
          organization yet. Travel Tracker is for IUPAT staff, and their days can be
          recorded here in the meantime.
        </p>
      )}

      {support.helpers.length > 0 && (
        <ul className="mt-4 max-w-2xl list-none space-y-2 p-0">
          {support.helpers.map((helper) => (
            <li
              key={helper.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-hairline bg-surface px-3 py-2"
            >
              <span className="min-w-0">
                <Link href={helper.href}>{helper.name}</Link>
                <span className="block text-xs text-ink-muted">
                  {[helper.email, helper.organization].filter(Boolean).join(" · ")}
                </span>
                {!helper.can_fill_out && (
                  <span className="block text-xs text-signal-display">
                    No access to Travel Tracker yet, so this does nothing until they have
                    Edit on it.
                  </span>
                )}
                {helper.granted_by && (
                  <span className="block text-xs text-ink-muted">
                    Added by {helper.granted_by}
                  </span>
                )}
              </span>
              <Button type="button" variant="ghost" size="sm" onClick={() => remove(helper.id)}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 max-w-md">
        <label htmlFor="travel-support-search">Add a person</label>
        <Autocomplete
          id="travel-support-search"
          className="mt-1"
          value={query}
          onValueChange={setQuery}
          suggestions={suggestions
            .filter((person) => person.id !== user.id)
            .map((person) => ({
              id: person.id,
              label: named.has(person.id) ? `${person.name} (already added)` : person.name,
              hint: [person.email, person.organization].filter(Boolean).join(" · ") || null,
            }))}
          loading={loading}
          onSelect={(suggestion) => add(Number(suggestion.id))}
          placeholder="Name, email, username or organization"
          hintMessage="Type a name, an email, a username or an organization."
          emptyMessage="No accounts match."
          aria-label="Find a person to fill out this Travel Tracker"
        />
      </div>
    </section>
  )
}

/**
 * App by app: what this person can do, and why. An override is set or cleared
 * here the moment the select changes; group membership is the checklist below.
 */
function AccessPanel({
  user,
  access,
  groups,
  levels,
}: {
  user: UserDetail
  access: UserAppAccess[]
  groups: UserAccessGroup[]
  levels: LevelOption[]
}) {
  const setOverride = (app: UserAppAccess, value: string) => {
    if (value === USE_GROUPS) {
      router.delete(`/admin/users/${user.id}/access/${app.key}`, { preserveScroll: true })
    } else {
      router.patch(
        `/admin/users/${user.id}/access/${app.key}`,
        { level: value },
        { preserveScroll: true },
      )
    }
  }

  const setMember = (group: UserAccessGroup, member: boolean) => {
    if (member) {
      router.post(
        `/admin/access_groups/${group.id}/members`,
        { user_id: user.id },
        { preserveScroll: true },
      )
    } else {
      router.delete(`/admin/access_groups/${group.id}/members/${user.id}`, {
        preserveScroll: true,
      })
    }
  }

  return (
    <section className="mt-10 border-t border-hairline pt-6">
      <h2>Access</h2>
      <p className="mt-1 text-sm text-ink-muted">
        {user.admin
          ? `${user.name} is an admin, so has Edit on every app whatever is set here. Groups and overrides take over if admin is removed.`
          : `What ${user.name} can do in each app, and why. An override beats every group, in either direction.`}
      </p>

      <div className="mt-4">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>App</TableHeaderCell>
              <TableHeaderCell>Has</TableHeaderCell>
              <TableHeaderCell>Override</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {access.map((app) => (
              <TableRow key={app.key}>
                <TableCell>
                  <div className="text-ink-display">{app.label}</div>
                  {app.iupat_only && (
                    <div className="text-xs text-ink-muted">IUPAT staff only</div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge tone={ACCESS_LEVEL_TONES[app.level]}>
                    {ACCESS_LEVEL_LABELS[app.level]}
                  </Badge>
                  <div className="mt-1 text-xs text-ink-muted">
                    {app.source_label.split(" — ")[1]}
                  </div>
                </TableCell>
                <TableCell>
                  <Select
                    aria-label={`Override for ${app.label}`}
                    wrapperClassName="w-40"
                    value={app.override ?? USE_GROUPS}
                    onChange={(event) => setOverride(app, event.target.value)}
                  >
                    <option value={USE_GROUPS}>Use groups</option>
                    {levels.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </Select>
                  {app.override && app.override_set_by && (
                    <div className="mt-1 text-xs text-ink-muted">
                      Set by {app.override_set_by}
                      {app.override_set_at ? ` ${formatDateTime(app.override_set_at)}` : ""}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <h3 className="mt-6">Groups</h3>
      {groups.length === 0 ? (
        <p className="mt-2 text-sm text-ink-muted">
          There are no groups. <Link href="/admin/access_groups/new">Create one</Link>.
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          {groups.map((group) => (
            <CheckboxOption
              key={group.id}
              checked={group.member}
              onChange={(event) => setMember(group, event.target.checked)}
              label={group.name}
              description={groupSummary(group, access)}
            >
              <Link
                href={`/admin/access_groups/${group.id}/edit`}
                className="text-xs text-ink-muted"
              >
                Open
              </Link>
            </CheckboxOption>
          ))}
        </div>
      )}
    </section>
  )
}

/** "Automations Edit · BUMP Training Read only · …" */
function groupSummary(group: UserAccessGroup, apps: UserAppAccess[]) {
  return apps
    .map((app) => `${app.label} ${ACCESS_LEVEL_LABELS[group.levels[app.key] ?? "no_access"]}`)
    .join(" · ")
}

/**
 * Duo fills most departments in, but not all of them and not always correctly.
 * A correction set here wins over the claim and survives every later sign-in;
 * clearing it hands the person back to whatever Duo says.
 */
function DepartmentOverride({
  user,
  suggestions,
}: {
  user: UserDetail
  suggestions: string[]
}) {
  const { data, setData, patch, processing } = useForm({
    department_override: user.department_override ?? "",
  })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    patch(`/admin/users/${user.id}/department`, { preserveScroll: true })
  }

  // Sent through the router rather than the form so the blank value goes out
  // immediately, instead of waiting for setData to settle.
  const clear = () => {
    setData("department_override", "")
    router.patch(
      `/admin/users/${user.id}/department`,
      { department_override: "" },
      { preserveScroll: true },
    )
  }

  return (
    <section className="mt-10 border-t border-hairline pt-6">
      <h2>Correct the department</h2>
      <p className="mt-1 text-sm text-ink-muted">
        {user.department_claim
          ? `Duo says “${user.department_claim}”. A correction here replaces it everywhere and is not overwritten at the next sign-in.`
          : "Duo has not sent a department for this person. Setting one here fills the gap, and Duo will not overwrite it."}
      </p>

      <form onSubmit={submit} className="mt-4 max-w-md space-y-2">
        <label htmlFor="department_override">Department</label>
        <Input
          id="department_override"
          list="department-suggestions"
          value={data.department_override}
          onChange={(event) => setData("department_override", event.target.value)}
          placeholder="e.g. Organizing"
        />
        <datalist id="department-suggestions">
          {suggestions.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
        <p className="text-xs text-ink-muted">
          Pick from a department already in use so the compliance grid groups people
          together, or type a new one.
        </p>

        <div className="flex items-center gap-3 pt-2">
          <Button type="submit" disabled={processing}>
            {processing ? "Saving…" : "Save department"}
          </Button>
          {user.department_override && (
            <Button type="button" variant="ghost" onClick={clear} disabled={processing}>
              Clear correction
            </Button>
          )}
        </div>
      </form>
    </section>
  )
}

/**
 * Ends every browser this person is signed in on.
 *
 * Duo is consulted at sign-in and never again, so removing somebody's Duo
 * account does not reach a session that is already open — this is the only
 * thing that does. It is also what finishes a demotion: the admin flag is read
 * live on every request, but the session outlives the role without this.
 */
function SessionRevocation({ user }: { user: UserDetail }) {
  const [working, setWorking] = React.useState(false)
  const open = user.open_sessions

  const signOut = () => {
    setWorking(true)
    router.patch(
      `/admin/users/${user.id}/sign_out_everywhere`,
      {},
      { preserveScroll: true, onFinish: () => setWorking(false) },
    )
  }

  return (
    <section className="mt-10 border-t border-hairline pt-6">
      <h2>Sessions</h2>
      <p className="mt-1 text-sm text-ink-muted">
        {open === 0
          ? `${user.name} has no sessions open. Signing out is still safe — it simply ends nothing.`
          : `${user.name} is signed in on ${open} ${open === 1 ? "browser" : "browsers"}. Sessions end on their own after ${SESSION_IDLE_LABEL} idle, or ${SESSION_MAX_LABEL} after sign-in whichever comes first. Sign them out here to end all of them now.`}
      </p>

      <div className="pt-4">
        <Button
          type="button"
          variant="danger"
          onClick={signOut}
          disabled={working || open === 0}
        >
          {working ? "Signing out…" : "Sign out everywhere"}
        </Button>
      </div>
    </section>
  )
}

// Mirrors Session::IDLE_TIMEOUT and Session::MAX_LIFETIME. Copy rather than a
// prop: these are policy the whole app shares, not a fact about this person.
const SESSION_IDLE_LABEL = "12 hours"
const SESSION_MAX_LABEL = "7 days"

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="text-sm text-ink-body">{value}</dd>
    </>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
